import * as React from 'react'
import {
  Action,
  async,
  AsyncState,
  div,
  fromJSX,
  loadedAsyncState,
  loadingAsyncState,
  none,
  Option,
  some,
  stateful,
  unloadedAsyncState,
  Widget
} from 'widgets-for-react'
import { LoaderCircle } from '../../accommodations/models/loading'
import {
  createBankAccount,
  getMerchant
} from '../../api/online_payment_platform'
import {
  BankAccount,
  MerchantResponse,
  UidObject
} from '../../types/online_payment_platform'
import { Compliance, Requirement } from '../../types/online_payment_platform'
import * as DL from '../../utils/datalayer'
import SimpleFormat from '../../utils/SimpleFormat'
import { prefixUrlWithLocale } from '../../utils/url'

type Props = { id: number; oppTermsUrl: string; iban: string }

type StepState =
  | {
      step: 'fetching'
    }
  | {
      step: 'start' | 'done'
      terms: boolean
      bankAccountCreation: AsyncState<UidObject>
      refetchingMerchant: boolean
      showForm: boolean
    }
  | {
      step: 'verify'
      nextRequirement: Requirement
    }

type State = {
  merchantResponse: AsyncState<MerchantResponse>
} & StepState

type StatefulProps = Props & {
  state: State
  setState: (onDone: Action<State>) => void
}

const initialState = (props: Props): State => ({
  step: 'fetching',
  merchantResponse: loadingAsyncState(_ => getMerchant(props.id))
})

const latestBankAccount = (merchant: MerchantResponse): Option<BankAccount> => {
  const latest = merchant.bank_accounts.sort((a, b) => b.created - a.created)[0]

  return !!latest ? some(latest) : none()
}

export const bankAccountRequirement = (
  merchant: MerchantResponse
): Requirement => {
  const latest = latestBankAccount(merchant)
  if (
    latest.v.kind === 'r' &&
    (latest.v.v.status === 'new' || latest.v.v.status === 'disapproved')
  ) {
    // this mimics a requirement to verify a bank account
    return {
      status: 'unverified',
      object_type: 'bank_account',
      object_redirect_url: latest.v.v.verification_url,
      type: 'bank_account.verification.required',
      object_uid: latest.v.v.uid,
      object_url: '' // this seems to not be in the bank account data
    }
  }
  return null
}
export const contactRequirement = (compliance: Compliance): Requirement =>
  compliance.requirements.find(
    r => r.object_type === 'contact' && r.status === 'unverified'
  )
export const phoneNumberRequirement = (compliance: Compliance): Requirement =>
  compliance.requirements.find(
    r => r.object_type === 'contact_phonenumber' && r.status === 'unverified'
  )
export const businessRequirement = (compliance: Compliance): Requirement =>
  compliance.requirements.find(
    r =>
      [
        'coc_extract.required',
        'organization_structure.required',
        'ubo.required',
        'ubo.verification.required'
      ].includes(r.type) && r.status === 'unverified'
  )
// the above check is like this because in their example the object_type is null

// this is used in case new requirement types are added without us being aware of them
export const unclassifiedRequirement = (compliance: Compliance): Requirement =>
  compliance.requirements.find(r => r.status === 'unverified')

export const urlToRedirect = (
  props: { id: number },
  merchant: MerchantResponse
): string => {
  const ubo = businessRequirement(merchant.merchant.compliance)
  if (!!ubo) {
    return ubo.object_redirect_url
  }
  const phone = phoneNumberRequirement(merchant.merchant.compliance)
  if (!!phone) {
    return phone.object_redirect_url
  }
  const bank = bankAccountRequirement(merchant)
  if (!!bank) {
    return prefixUrlWithLocale(`/admin/${props.id}/account_verification`)
  }
  const contact = contactRequirement(merchant.merchant.compliance)
  if (!!contact) {
    return prefixUrlWithLocale(`/admin/${props.id}/identity_verification`)
  }
  const unclassified = unclassifiedRequirement(merchant.merchant.compliance)
  if (!!unclassified) {
    return unclassified.object_redirect_url
  }
  return prefixUrlWithLocale(`/admin/${props.id}/edit#payment`)
}

export const nextToVerify = (merchant: MerchantResponse): Requirement =>
  phoneNumberRequirement(merchant.merchant.compliance) ||
  businessRequirement(merchant.merchant.compliance) ||
  bankAccountRequirement(merchant) ||
  contactRequirement(merchant.merchant.compliance) ||
  unclassifiedRequirement(merchant.merchant.compliance)

const initialStateAfterMerchantLoaded = (merchant: MerchantResponse): State => {
  const merchantResponse = loadedAsyncState(merchant)
  const requirement = nextToVerify(merchant)
  const bankAccountMissing = merchant.bank_accounts.length === 0
  const state: StepState =
    !requirement && !bankAccountMissing
      ? {
          step: 'done',
          bankAccountCreation: unloadedAsyncState(),
          refetchingMerchant: false,
          terms: false,
          showForm: false
        }
      : requirement.type === 'bank_account.required' || bankAccountMissing
      ? {
          step: 'start',
          bankAccountCreation: unloadedAsyncState(),
          refetchingMerchant: false,
          terms: false,
          showForm: true
        }
      : {
          step: 'verify',
          nextRequirement: requirement
        }
  return {
    ...state,
    merchantResponse
  }
}

const loaderWidgets = (
  props: Props,
  state: State
): Array<Widget<Action<State>>> => {
  switch (state.step) {
    case 'start':
    case 'done':
      return [
        async<UidObject>()(state.bankAccountCreation).map<Action<State>>(
          updateBankAccount => s => {
            if (s.step === 'start' || s.step === 'done') {
              const newBankAccount = updateBankAccount(s.bankAccountCreation)
              if (
                newBankAccount.kind === 'loaded' &&
                s.merchantResponse.kind === 'loaded'
              ) {
                return {
                  ...s,
                  bankAccountCreation: newBankAccount,
                  refetchingMerchant: true,
                  merchantResponse: loadingAsyncState(_ =>
                    getMerchant(props.id)
                  )
                }
              } else {
                return {
                  ...s,
                  merchantResponse:
                    newBankAccount.kind === 'failed'
                      ? loadingAsyncState(_ => getMerchant(props.id))
                      : s.merchantResponse,
                  bankAccountCreation: newBankAccount
                }
              }
            }
            return s
          }
        )
      ]
    default:
      return []
  }
}

const BankAccountForm = (
  props: StatefulProps & { showTerms: boolean }
): JSX.Element => {
  if (
    (props.state.step !== 'start' && props.state.step !== 'done') ||
    props.state.merchantResponse.kind !== 'loaded'
  ) {
    return <></>
  }

  return (
    <>
      {props.state.bankAccountCreation.kind === 'failed' ? (
        <div className="box box--s box--bordered box--warning">
          <div className="box__body">
            <SimpleFormat
              text={I18n.t('users.opp_onboarding.start_form_error')}
            />
          </div>
        </div>
      ) : (
        <></>
      )}

      {props.showTerms ? (
        <div className="form-row">
          <div className="form-cell checkbox-list">
            <label htmlFor="opp_accept_terms">
              <input
                type="checkbox"
                id="opp_accept_terms"
                checked={props.state.terms}
                onChange={e => {
                  props.setState(s => {
                    if (s.step === 'start' || s.step === 'done') {
                      return {
                        ...s,
                        terms: !s.terms
                      }
                    }
                  })
                }}
              />
              <span
                dangerouslySetInnerHTML={{
                  __html: I18n.t('users.opp_onboarding.accept_terms', {
                    opp_terms_path: props.oppTermsUrl
                  })
                }}
              />
            </label>
          </div>
        </div>
      ) : (
        <></>
      )}

      <p>{I18n.t('users.opp_onboarding.business_account_info')}</p>
      <div className="form-row">
        <div className="form-cell">
          <button
            className="btn btn--primary"
            disabled={
              (props.showTerms && !props.state.terms) ||
              props.state.bankAccountCreation.kind === 'loading'
            }
            onClick={e => {
              e.preventDefault()
              props.setState(s => {
                if (s.step === 'start' || s.step === 'done') {
                  return {
                    ...s,
                    bankAccountCreation: loadingAsyncState(_ =>
                      createBankAccount(props.id)
                    )
                  }
                }
                return s
              })
            }}
          >
            {I18n.t('users.opp_onboarding.start_verification')}
          </button>
        </div>
      </div>
    </>
  )
}

const startElement = (props: StatefulProps) => {
  if (
    (props.state.step !== 'start' && props.state.step !== 'done') ||
    props.state.merchantResponse.kind !== 'loaded'
  ) {
    return <></>
  }
  return (
    <>
      <p>
        {props.state.step === 'start'
          ? I18n.t('users.opp_onboarding.intro')
          : ''}
      </p>
      <p>
        {props.state.step === 'start'
          ? I18n.t('users.opp_onboarding.intro2')
          : ''}
      </p>
      <h2 className="form-legend">{I18n.t('users.opp_onboarding.title')}</h2>
      <BankAccountForm {...props} showTerms={true} />
    </>
  )
}

const verifyElement = (props: StatefulProps) => {
  if (
    props.state.step !== 'verify' ||
    props.state.merchantResponse.kind !== 'loaded'
  ) {
    return <></>
  }
  const nextRequirementKey = props.state.nextRequirement.type.split('.')[0]
  return (
    <>
      <div className="box box--s box--bordered box-payments">
        <div className="box__body">
          <p
            dangerouslySetInnerHTML={{
              __html: I18n.t('users.opp_onboarding.to_verify', {
                status: I18n.t(
                  `users.opp_onboarding.requirements.${nextRequirementKey}`,
                  {
                    defaultValue: nextRequirementKey
                  }
                )
              })
            }}
          />
        </div>
      </div>

      <p>{I18n.t('users.opp_onboarding.intro')}</p>

      <div className="form-row">
        <div
          className="form-cell"
          onClick={_ => {
            DL.push({
              event: 'OPP',
              category: 'Onboarding OPP',
              action: 'Continue verification'
            })
          }}
        >
          <a
            href={urlToRedirect(
              { id: props.id },
              props.state.merchantResponse.value
            )}
            className="btn btn--primary"
          >
            {I18n.t('users.opp_onboarding.continue_verification')}
          </a>
        </div>
      </div>
    </>
  )
}

const misMatchesObfuscatedIban = (
  bankAccount: BankAccount,
  iban: string
): boolean =>
  !!bankAccount.account &&
  !!bankAccount.account.account_iban &&
  !!iban &&
  !iban
    .replace(/\W/g, '')
    .match(
      new RegExp(
        `^${bankAccount.account.account_iban
          .toLowerCase()
          .replace(/\*+/g, '.+')}$`,
        'i'
      )
    )

export const stillPendingFromMerchant = (
  merchant: MerchantResponse
): string[] => {
  const bankAcccount = latestBankAccount(merchant)
  return merchant.merchant.compliance.requirements
    .filter(
      r =>
        r.status === 'pending' &&
        (!r.object_type || !r.object_type.includes('bank_account'))
    )
    .map(r =>
      I18n.t(`users.opp_onboarding.requirements.${r.type.split('.')[0]}`, {
        defaultValue: r.type.split('.')[0]
      })
    )
    .concat(
      bankAcccount.v.kind === 'r' && bankAcccount.v.v.status === 'pending'
        ? [I18n.t('users.opp_onboarding.requirements.bank_account')]
        : []
    )
}

const doneElement = (props: StatefulProps) => {
  if (
    props.state.step !== 'done' ||
    props.state.merchantResponse.kind !== 'loaded'
  ) {
    return <></>
  }
  const stillPending = stillPendingFromMerchant(
    props.state.merchantResponse.value
  ).join(', ')
  const relevantBankAccount = props.state.merchantResponse.value.bank_accounts.find(
    ba => ba.is_default
  )
  const isBusiness =
    props.state.merchantResponse.value.merchant.type === 'business'

  return (
    <>
      <p>{I18n.t('users.opp_onboarding.no_more_action')}</p>
      <p>{I18n.t('users.opp_onboarding.payment_opp')}</p>
      {misMatchesObfuscatedIban(
        props.state.merchantResponse.value.bank_accounts.find(
          x => x.is_default
        ),
        props.iban
      ) ? (
        <div className="box box--s box--bordered box--warning">
          <div className="box__body">
            <SimpleFormat text={I18n.t('users.opp_onboarding.iban_mismatch')} />
          </div>
        </div>
      ) : (
        <></>
      )}
      <div className="box box--s box--bordered box-payments">
        <div className="box__body">
          <p>
            {stillPending !== ''
              ? I18n.t('users.opp_onboarding.still_pending', {
                  statuses: stillPending
                })
              : I18n.t('users.opp_onboarding.all_done')}
          </p>
        </div>
      </div>

      <p>{I18n.t('users.opp_onboarding.intro')}</p>

      <div>
        <h2>{I18n.t('users.opp_onboarding.linked_bank_account')}</h2>

        <div className="box">
          <p className="form-row no-mb">
            <span className="form-cell cell-20">
              <b>{I18n.t('users.opp_onboarding.iban')}</b>
            </span>
            <span className="form-cell cell-80">
              {relevantBankAccount.account.account_iban}
            </span>
          </p>
          <p className="form-row no-mb">
            <span className="form-cell cell-20">
              <b>{I18n.t('users.opp_onboarding.business_account')}</b>
            </span>
            <span className="form-cell cell-80">
              {I18n.t(`users.opp_onboarding.${isBusiness ? 'yes' : 'no'}`)}
            </span>
          </p>
        </div>
      </div>
      {isBusiness ? (
        <div className="box box--s box--bordered box-payments">
          <div className="box__body">
            <p>{I18n.t('users.opp_onboarding.business_to_consumer')}</p>
          </div>
        </div>
      ) : (
        <></>
      )}

      {!props.state.showForm ? (
        <a
          href="#"
          onClick={e => {
            e.preventDefault()
            props.setState(s =>
              s.step === 'done'
                ? {
                    ...s,
                    showForm: true
                  }
                : s
            )
          }}
        >
          {I18n.t('users.opp_onboarding.change_account')}
        </a>
      ) : (
        <></>
      )}

      {props.state.showForm ? (
        <>
          <h2 className="form-legend">
            {I18n.t('users.opp_onboarding.second_title')}
          </h2>
          <BankAccountForm {...props} showTerms={false} />
        </>
      ) : (
        <></>
      )}
    </>
  )
}

const fetchingElement = (merchant: AsyncState<MerchantResponse>) =>
  merchant.kind === 'failed' ||
  merchant.kind === 'connection-error' ||
  merchant.kind === 'unauthorized' ? (
    <div className="box box--s box--bordered box--warning">
      <div className="box__body">
        <SimpleFormat text={I18n.t('users.opp_onboarding.start_form_error')} />
      </div>
    </div>
  ) : (
    <LoaderCircle />
  )

const renderStep = (props: StatefulProps) => {
  switch (props.state.step) {
    case 'fetching':
      return fetchingElement(props.state.merchantResponse)
    case 'start':
      return startElement(props)
    case 'verify':
      return verifyElement(props)
    case 'done':
      return doneElement(props)
    default:
      return <></>
  }
}

const merchantLoader = (props: Props, s0: State): Widget<Action<State>> =>
  async<MerchantResponse>()(s0.merchantResponse).map(uc => s => {
    const merchantResponse = uc(s.merchantResponse)
    if (merchantResponse.kind === 'loaded') {
      if ((s.step === 'start' || s.step === 'done') && s.refetchingMerchant) {
        DL.push({
          event: 'OPP',
          category: 'Onboarding OPP',
          action:
            s.step === 'start' ? 'Start onboarding' : 'Change bankaccount',
          label:
            merchantResponse.value.merchant.type === 'business'
              ? 'zakelijk'
              : 'prive'
        })
        window.location.href = urlToRedirect(props, merchantResponse.value)
      } else {
        return initialStateAfterMerchantLoaded(merchantResponse.value)
      }
    }
    return {
      ...s,
      merchantResponse
    }
  })

const stepRenderingWidget = (props: Props, s0: State): Widget<Action<State>> =>
  fromJSX(setState => renderStep({ ...props, state: s0, setState }))

export class Onboarding extends React.Component<Props> {
  render() {
    return stateful<State>({ key: 'onboarding-component' })(s0 => {
      return div<Action<State>>({ className: 'grid-100 tablet-grid-100' })([
        ...loaderWidgets(this.props, s0),
        merchantLoader(this.props, s0),
        stepRenderingWidget(this.props, s0)
      ]).map(a => a(s0))
    })(initialState(this.props)).run(_ => null)
  }
}

export default (props: Props): JSX.Element => <Onboarding {...props} />
